import {
  Box,
  Button,
  Container,
  Card,
  Grid,
  Col,
  Stack,
  Divider,
  Group,
  Modal,
  Text,
  TextInput,
  PasswordInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { Link, Location, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth.context";
import InputField, { InputFieldMasks, InputFieldTypes } from "../components/input_field";
import { AUTH_LOGIN, FORGOT_PASSWORD, RECEIVE_OTP } from "../services/auth";
import { showNotification } from "@mantine/notifications";
import { useApp } from "../contexts/app.context";
import { formatMilliseconds, phoneCountryCodes } from "../utility/util";
import moment from "moment";
import { api } from "../services/api";
import OtpFields from "../components/otp_fields";
import { useMediaQuery } from "@mantine/hooks";
import { useNoCaptcha } from "../hooks/useNoCaptcha";

export default function Login() {
  const { login, signIn, companyHost, user } = useAuth();
  const { configs } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const {getNoCaptcha} = useNoCaptcha();

  const isMobile = useMediaQuery('(max-width: 987px)');

  const [loginType, setLoginType] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [forgot, setForgot] = useState(null);
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState("55");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRegenerateTimeout, setOtpRegenerateTimeout] = useState(0);
  const [otpSent, setOtpSent] = useState(null);

  const state = location.state as { from: Location };
  const from = state ? state.from.pathname : "/";

  const confirmForgot = e => {
    e.preventDefault();
    const email = forgot.email;

    FORGOT_PASSWORD(email)
      .then(() => {
        setForgot(null);
        showNotification({
          title: "Recuperação de senha",
          message: `Foi enviado um e-mail para ${email} com instruções para recuperar sua senha`,
        });
      })
      .catch(err => {
        showNotification({ title: "Ops", message: err.message, color: "red" });
      });
  };

  const generateOtp = () => {
    setOtpSent(moment().add(2, "minutes").format());
    RECEIVE_OTP({
      country_code: countryCode,
      phone,
    })
      .catch(err => {
        showNotification({ message: err.message, color: "red" });
        setOtpSent(null);
      });
  };

  useEffect(() => {
    if (otpSent) {
      const otpInterval = setInterval(() => {
        const diff = moment(otpSent).diff(moment(), "seconds");
        setOtpRegenerateTimeout(diff > 0 ? diff : 0);
      }, 200);
      return () => clearInterval(otpInterval);
    }
  }, [otpSent]);

  useEffect(() => {
    setLoginType(companyHost ? "phone" : "email");
  }, [companyHost]);

  if (user) return <Navigate to="/" state={{ from }} />;

  return (
    <Container
      fluid
      style={{
        minHeight: "100vh",
        padding: 0,
        background: "#FAFAFA",
      }}
    >
      <Grid grow gutter={0} style={{ height: "100vh", justifyContent: "center", alignItems: "center", }}>
        {/* LEFT COLUMN — LOGIN FORM */}
        <Col
          span={12}
          md={isMobile ? 12 : 5}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: 32,
          }}
        >
          <Card
            p="38px"
            withBorder
            shadow="sm"
            style={{
              borderRadius: 12,
              width: "100%",
              maxWidth: 420,
              marginTop: 40,
            }}
          >
            <Stack spacing="lg">
              <Box>
                <Title order={2}>Entrar</Title>

                {loginType === "email" && (
                  <Group mt="sm" position="apart">
                    <Text color="gray">Ainda não tem uma conta?</Text>
                    <Link
                      to={
                        companyHost
                          ? `/cadastrar/cliente${from ? `?from=${from}` : ""}`
                          : `/cadastrar${from ? `?from=${from}` : ""}`
                      }
                    >
                      <Text color="blue">Criar conta</Text>
                    </Link>
                  </Group>
                )}
              </Box>

              {/* ————————————————
                  PHONE LOGIN + OTP
              ———————————————— */}
              <Box>
                {loginType === "phone" ? (
                  otpSent ? (
                    <Box mt="md">
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();

                          if (signingUp) {
                            const captchaToken = await getNoCaptcha("signup");
                            api
                              .post("/auth/signup", {
                                general: { name, email, phone, phone_country_code: countryCode },
                                others: { otp, captchaToken },
                              })
                              .then(({ data: { user } }) => {
                                signIn(user);
                              })
                              .catch(err => showNotification({ message: err.message }));
                          } else {
                            AUTH_LOGIN({ phone, country_code: countryCode, otp })
                              .then(({ user }) => signIn(user))
                              .catch(() => setSigningUp(true));
                          }
                        }}
                      >
                        {signingUp ? (
                          <>
                            <Text ta="center" mb="xl" size="sm">
                              Complete seus dados para continuar
                            </Text>

                            <Stack>
                              <InputField
                                title="Seu Nome"
                                mb="lg"
                                name="vl"
                                value={name}
                                onChange={({ vl }) => setName(vl)}
                              />
                              <InputField
                                title="Seu E-mail"
                                mb="lg"
                                name="vl"
                                value={email}
                                onChange={({ vl }) => setEmail(vl)}
                              />
                            </Stack>

                            <Button
                              mt="md"
                              type="submit"
                              size="md"
                              fullWidth
                              disabled={name.length < 3 || !email.includes("@")}
                            >
                              Entrar
                            </Button>

                            <UnstyledButton
                              mt="md"
                              onClick={() => {
                                setOtpSent(false);
                                setSigningUp(false);
                              }}
                              style={{ width: "100%" }}
                            >
                              <Text underline size="sm" align="right">
                                Voltar
                              </Text>
                            </UnstyledButton>
                          </>
                        ) : (
                          <>
                            <Text ta="center" mb="xl" size="sm">
                              Insira o código enviado para seu Whatsapp{" "}
                              <b>
                                +{countryCode}
                                {(phone ?? "").replace(/ |\-|\(|\)|\_/g, "")}
                              </b>
                            </Text>

                            <Group position="center">
                              <OtpFields length={4} value={otp} onChange={setOtp} />
                            </Group>

                            <Button mt="md" type="submit" size="md" fullWidth disabled={otp.length < 4}>
                              Entrar
                            </Button>

                            <Text mt="xl" ta="center" size="sm">
                              Ainda não recebeu o código?{" "}
                              {otpRegenerateTimeout > 0 ? (
                                <b>
                                  Envie novamente em {formatMilliseconds(otpRegenerateTimeout * 1000)}
                                </b>
                              ) : (
                                <a href="#" onClick={generateOtp}>
                                  Re-enviar Código
                                </a>
                              )}
                            </Text>

                            <UnstyledButton mt="md" onClick={() => setOtpSent(false)} style={{ width: "100%" }}>
                              <Text underline size="sm" align="right">
                                Voltar
                              </Text>
                            </UnstyledButton>
                          </>
                        )}
                      </form>
                    </Box>
                  ) : (
                    <Box mt="md">
                      <form
                        onSubmit={e => {
                          e.preventDefault();
                          generateOtp();
                        }}
                      >
                        <Group>
                          <div style={{ flex: 1 }}>
                          <InputField
                            title="País"
                            mb="lg"
                            name="vl"
                            autoComplete="tel-country-code"
                            fieldType={InputFieldTypes.SELECT}
                            value={countryCode}
                            onChange={({ vl }) => setCountryCode(vl)}
                            nothingFound="No country code found"
                            options={phoneCountryCodes}
                          />
                          </div>

                          <InputField
                            title="Whatsapp"
                            mb="lg"
                            name="vl"
                            inputMode="numeric"
                            style={{ flex: 1 }}
                            mask={countryCode === "55" ? InputFieldMasks.CUSTOM : undefined}
                            customMask={
                              phone?.replace(/ |\(|\)|\-/g, "").length < 11
                                ? "(99) 9999-99999"
                                : "(99) 99999-9999"
                            }
                            value={phone}
                            onChange={({ vl }) => setPhone(vl)}
                          />
                        </Group>

                        <Button
                          type="submit"
                          size="md"
                          fullWidth
                          disabled={(phone ?? "").replace(/[^0-9]/g, "").length < 8}
                        >
                          Entrar
                        </Button>
                      </form>
                    </Box>
                  )
                ) : (
                  /* ————————————————
                       EMAIL LOGIN
                  ———————————————— */
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      login({ email, password }, () => {
                        navigate(from, { replace: true });
                      });
                    }}
                  >
                    <TextInput
                      mt="md"
                      label="E-mail"
                      value={email}
                      name="email"
                      onChange={e => setEmail(e.target.value)}
                    />
                    <PasswordInput
                      mt="md"
                      label="Senha"
                      value={password}
                      name="password"
                      onChange={e => setPassword(e.target.value)}
                    />

                    <UnstyledButton mt="md" onClick={() => setForgot({ email: "" })} style={{ width: "100%" }}>
                      <Text underline size="sm" align="right">
                        Esqueci minha senha
                      </Text>
                    </UnstyledButton>

                    <Button type="submit" mt="xl" size="md" fullWidth>
                      Entrar
                    </Button>
                  </form>
                )}
              </Box>

              <Group mt="md" position="center">
                <Text
                  size="xs"
                  color="gray"
                  style={{ cursor: "pointer" }}
                  onClick={() => window.open("https://aprova.ai/privacy-policy/", "_blank")}
                >
                  Políticas de Privacidade
                </Text>
                <Divider orientation="vertical" />
                <Text
                  size="xs"
                  color="gray"
                  style={{ cursor: "pointer" }}
                  onClick={() => window.open("https://aprova.ai/terms-of-use/", "_blank")}
                >
                  Termos de Uso
                </Text>
              </Group>
            </Stack>
          </Card>
        </Col>

        {/* RIGHT COLUMN — FULL BACKGROUND IMAGE */}
        {!isMobile && <Col span={12} md={7} style={{ padding: 0, height: '100%' }}>
          <Box
            style={{
              width: "100%",
              height: "100%",
              backgroundImage: "url('/assets/login-bg.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: 'relative',
            }}
          >
            <Box style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 100%)' }} />
          </Box>
        </Col>}
      </Grid>

      {/* FORGOT PASSWORD MODAL */}
      <Modal size="sm" opened={!!forgot} onClose={() => setForgot(null)}>
        <form onSubmit={confirmForgot}>
          <InputField
            name="email"
            placeholder="Digite seu e-mail"
            title="E-mail"
            value={forgot?.email}
            onChange={({ email }) => setForgot(f => ({ ...f, email }))}
          />

          <Group position="right" mt="lg">
            <Button color="dark" variant="light" type="button" mr="xs" onClick={() => setForgot(null)}>
              Cancelar
            </Button>
            <Button color="dark" variant="filled" type="submit">
              Recuperar Senha
            </Button>
          </Group>
        </form>
      </Modal>
    </Container>
  );
}
